import React, { useMemo } from 'react';
import { DecisionGraph } from '@imminently/decisively-core';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import type { Control, ControlType } from '@imminently/interview-sdk';
import { CONTROL_TYPES } from '@imminently/interview-sdk';
import { useFullRelease } from '@/common/hooks_useFullRelease';
import type { Interview, InterviewStep } from '@packages/commons';
import { setPendingInterviewNavigation } from '@/pages/models/release/Interview/Designer/provider/interviewNavigationSignal';
import { useCommandPaletteContext } from '../CommandPaletteContext';
import { CommandList } from '../components/CommandList';
import {
  useRegisterCommands,
  useRegisterPage,
  useRegisterShortcut,
  usePluginNavigation,
  useDebouncedSearch,
} from '../hooks';
import type { Command as CommandType } from '../types';

const PLUGIN_ID = 'interview-controls-plugin';
const CONTROLS_PAGE = 'interview-controls';
const CONTROLS_PLACEHOLDER = 'Search by attribute name...';

// ---------------------------------------------------------------------------
// Type name lookup from the SDK constant
// ---------------------------------------------------------------------------

const CONTROL_TYPE_NAMES: Partial<Record<ControlType, string>> = Object.fromEntries(
  Object.values(CONTROL_TYPES).map((info) => [info.id, info.name]),
) as Partial<Record<ControlType, string>>;

const getTypeName = (type: string): string => CONTROL_TYPE_NAMES[type as ControlType] ?? type;

// ---------------------------------------------------------------------------
// ControlHit — a flat record of a single attribute-bound control
// ---------------------------------------------------------------------------

interface ControlHit {
  /** Unique key for the command list */
  id: string;
  interviewId: string;
  interviewName: string;
  stepId: string;
  /** Control ID within the layout */
  controlId: string;
  /** CtrlPath: path of [containerControlId, propertyName] pairs to reach the
   *  array that contains this control. Empty for root-level controls. */
  ctrlPath: Array<[string, string]>;
  /** Breadcrumb label segments for the description (Interview / Step / ...) */
  breadcrumb: string[];
  /** The control's own label (or attribute name as fallback) */
  controlLabel: string;
  typeName: string;
  attributeName: string;
}

// ---------------------------------------------------------------------------
// Recursive extractors
// ---------------------------------------------------------------------------

/** Property names on container controls that hold child Control arrays. */
const CONTAINER_CHILD_PROPS = ['controls', 'template', 'certain', 'uncertain', 'outcome_true', 'outcome_false'] as const;

function extractFromControls(
  controls: Control[],
  stepId: string,
  breadcrumb: string[],
  /** CtrlPath accumulated from the root of the layout to this array */
  parentCtrlPath: Array<[string, string]>,
  interviewId: string,
  interviewName: string,
  attributeMap: Map<string, string>,
  results: ControlHit[],
): void {
  for (const ctrl of controls) {
    const c = ctrl as any;

    // Record attribute-bound controls
    if (typeof c.attribute === 'string' && c.attribute) {
      const attributeName = attributeMap.get(c.attribute) ?? c.attribute;
      const controlLabel = c.label || attributeName;
      results.push({
        id: `${interviewId}-${c.id}`,
        interviewId,
        interviewName,
        stepId,
        controlId: c.id,
        ctrlPath: parentCtrlPath,
        breadcrumb,
        controlLabel,
        typeName: getTypeName(ctrl.type),
        attributeName,
      });
    }

    // Recurse into all known container child lists, extending the CtrlPath
    const containerLabel = c.label || getTypeName(ctrl.type);
    const childBreadcrumb = [...breadcrumb, containerLabel];

    for (const prop of CONTAINER_CHILD_PROPS) {
      const children: Control[] | undefined = c[prop];
      if (Array.isArray(children) && children.length > 0) {
        extractFromControls(
          children,
          stepId,
          childBreadcrumb,
          [...parentCtrlPath, [c.id, prop]],
          interviewId,
          interviewName,
          attributeMap,
          results,
        );
      }
    }
  }
}

function extractFromSteps(
  steps: InterviewStep[],
  breadcrumb: string[],
  interviewId: string,
  interviewName: string,
  attributeMap: Map<string, string>,
  results: ControlHit[],
): void {
  for (const step of steps) {
    const stepBreadcrumb = [...breadcrumb, step.title];

    // Recurse into nested sub-steps first
    if (step.steps?.length) {
      extractFromSteps(step.steps, stepBreadcrumb, interviewId, interviewName, attributeMap, results);
    }

    // Then process each layout (screen) in the step
    for (const layout of step.layout ?? []) {
      const layoutBreadcrumb = layout.title ? [...stepBreadcrumb, layout.title] : stepBreadcrumb;
      extractFromControls(
        layout.controls ?? [],
        step.id,
        layoutBreadcrumb,
        [],
        interviewId,
        interviewName,
        attributeMap,
        results,
      );
    }
  }
}

// ---------------------------------------------------------------------------
// useInterviewControlHits — derives the flat list of all attribute-bound
// controls from all interviews in the current release.
// ---------------------------------------------------------------------------

const useInterviewControlHits = (): ControlHit[] => {
  const release = useFullRelease();

  return useMemo(() => {
    if (!release) return [];

    // Build attribute UUID → human-readable name map from the rule graph
    const attributeMap = new Map<string, string>();
    const graph = release.rule_graph ? DecisionGraph.from(release.rule_graph as any) : undefined;
    for (const id of graph?.ids() ?? []) {
      const description = graph!.field(id, "description") as string | undefined;
      if (description) attributeMap.set(graph!.deref(id), description);
    }

    const results: ControlHit[] = [];

    for (const interview of release.interviews ?? []) {
      if (!interview.id || !interview.name) continue;
      extractFromSteps(
        interview.steps ?? [],
        [interview.name],
        interview.id,
        interview.name,
        attributeMap,
        results,
      );
    }

    return results;
  }, [release?.interviews, release?.rule_graph]);
};

// ---------------------------------------------------------------------------
// InterviewControlsPage — displayed when the palette navigates to CONTROLS_PAGE
// ---------------------------------------------------------------------------

const InterviewControlsPage: React.FC = () => {
  const { close } = useCommandPaletteContext();
  const dispatch = useDispatch();
  const hits = useInterviewControlHits();
  const search = useDebouncedSearch();

  const filtered = useMemo(() => {
    if (!search) return hits;
    const term = search.toLowerCase();
    return hits.filter(
      (h) =>
        h.attributeName.toLowerCase().includes(term) ||
        h.controlLabel.toLowerCase().includes(term),
    );
  }, [hits, search]);

  const commands = useMemo((): CommandType[] =>
    filtered.map((hit) => ({
      id: hit.id,
      label: hit.controlLabel,
      description: `${hit.typeName} · ${hit.breadcrumb.join(' / ')}`,
      category: hit.interviewName,
      keywords: [hit.attributeName, hit.typeName, ...hit.breadcrumb],
      primary: {
        id: `${hit.id}-open`,
        label: 'Open in Interview Designer',
        action: () => {
          // Set the signal BEFORE navigating so IdProvider picks it up on
          // mount (fresh navigation) or on interview change (already mounted).
          setPendingInterviewNavigation({
            interviewId: hit.interviewId,
            stepId: hit.stepId,
            controlId: hit.controlId,
            ctrlPath: hit.ctrlPath,
          });
          // Keep the existing localStorage mechanism so the interview is also
          // restored correctly when IdProvider is freshly mounted.
          localStorage.setItem('rememberedInterview', JSON.stringify(hit.interviewId));
          dispatch(push('/build/design'));
          close();
        },
      },
    })),
  [filtered, dispatch, close]);

  return (
    <CommandList
      commands={commands}
      emptyMessage={search ? 'No controls found for that attribute.' : 'Type an attribute name to search.'}
    />
  );
};

// ---------------------------------------------------------------------------
// InterviewControlsPlugin — registers the global command + keyboard shortcut
// ---------------------------------------------------------------------------

/**
 * InterviewControlsPlugin - Find where attributes are used in interview controls
 *
 * Opens a searchable page that lists every attribute-bound control across all
 * interviews in the current release. Search filters by attribute name. Each
 * result shows the control type and label, with a breadcrumb path of
 * Interview → Step → Container hierarchy as the description.
 *
 * Selecting a result navigates to the Interview Designer with that interview
 * pre-selected.
 */
export const InterviewControlsPlugin: React.FC = () => {
  const { navigateTo, openAndNavigate } = usePluginNavigation(PLUGIN_ID);

  const globalCommands = useMemo((): CommandType[] => [
    {
      id: 'open-interview-controls-search',
      label: 'Find Attribute in Interview Controls',
      description: 'Search where attributes are used in interview controls',
      category: 'Navigation',
      shortcut: 'mod+alt+a',
      keywords: ['new!', 'experimental', 'interview', 'attribute', 'control', 'find', 'usage', 'where'],
      primary: {
        id: 'open-interview-controls-search-action',
        label: 'Search Interview Controls',
        action: () => navigateTo(CONTROLS_PAGE, CONTROLS_PLACEHOLDER),
      },
    },
  ], [navigateTo]);

  useRegisterCommands({ pluginId: PLUGIN_ID, commands: globalCommands });
  useRegisterPage({ pluginId: PLUGIN_ID, pageId: CONTROLS_PAGE, component: InterviewControlsPage });

  useRegisterShortcut({
    shortcut: 'mod+alt+a',
    label: 'Go to Interview Controls',
    handler: () => openAndNavigate(CONTROLS_PAGE, CONTROLS_PLACEHOLDER),
  });

  return null;
};
